import { StoreSnapshot } from '@tldraw/tldraw'
import { useRootStore } from 'stores/RootStore'
import { DesignClient } from 'api/clients/DesignClient'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCallback } from 'react'

export const useApplyDesign = ({ fileId }: { fileId: string }) => {
  const { designStore, files, generationTimestampStore } = useRootStore()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const setFileGenerating = useCallback(
    (isGenerating: boolean) => {
      optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileId, (f) => ({
        ...f,
        is_generating: isGenerating,
        image_url_generating: isGenerating,
      }))
      updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, fileId, (file) => ({
        ...file,
        is_generating: isGenerating,
        image_url_generating: isGenerating,
      }))
    },
    [queryClient, workspaceId, fileId],
  )

  const applyDesign = async (snapshot: StoreSnapshot<any>) => {
    setFileGenerating(true)
    generationTimestampStore.startGeneration(fileId, 'default')

    try {
      await DesignClient.updateDesign(fileId, { content: snapshot })
      if (designStore && designStore.fileId === fileId) {
        await designStore.loadSnapshot(snapshot)
      }

      files.fetch(fileId, true)
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(calendarKeys.detail(fileId))
    } catch {
      setFileGenerating(false)
      generationTimestampStore.completeGeneration(fileId)
      showToastMessage('Unable to apply design', 'error')
    }
  }

  return {
    applyDesign,
  }
}
